import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import NotificationBell from "./NotificationBell";
import pickleballIcon from "./pickleball.png";


const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

export default function ChatList({ onRoomClick, unreadByRoom = {}, onHome }) {
  const { activeTeam, user } = useAuth();
  const myTeam = activeTeam?.team_name || "";
  const emailPrefix = user?.email ? user.email.split("@")[0] : "";
  const [rooms, setRooms]           = useState([]);
  const [teamLogoMap, setTeamLogoMap] = useState({});
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const fetchRooms = useCallback(async (teamName, emailPfx) => {
    setLoading(true);
    setError("");

    // .or() 필터는 팀 이름에 공백이 있으면 오작동 → 두 쿼리로 분리 후 병합
    // 이메일 prefix도 함께 조회하여 회원모집 채팅방도 표시
    const queries = [
      supabase.from("chat_rooms").select("*").eq("team1_name", teamName).order("created_at", { ascending: false }),
      supabase.from("chat_rooms").select("*").eq("team2_name", teamName).order("created_at", { ascending: false }),
    ];
    if (emailPfx && emailPfx !== teamName) {
      queries.push(
        supabase.from("chat_rooms").select("*").eq("team1_name", emailPfx).order("created_at", { ascending: false }),
        supabase.from("chat_rooms").select("*").eq("team2_name", emailPfx).order("created_at", { ascending: false }),
      );
    }
    const results = await Promise.all(queries);

    const firstError = results.find((r) => r.error);
    if (firstError) {
      setError("채팅 목록을 불러오지 못했어요: " + firstError.error.message);
      setLoading(false);
      return;
    }

    // 모든 결과 합치고 ID 중복 제거
    const seenId = new Set();
    const allRooms = results.flatMap((r) => r.data || []).filter((r) => {
      if (seenId.has(r.id)) return false;
      seenId.add(r.id);
      return true;
    }).sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at));

    // 같은 두 팀 사이의 중복 채팅방 제거 (가장 최근 것만 유지)
    const seenPair = new Set();
    const roomsData = allRooms.filter((r) => {
      const pair = [r.team1_name, r.team2_name].sort().join("||");
      if (seenPair.has(pair)) return false;
      seenPair.add(pair);
      return true;
    });

    if (!roomsData?.length) {
      setRooms([]);
      setLoading(false);
      return;
    }

    // 일정 조회 → 경기일 + 5일 지난 방은 숨김
    const roomIds = roomsData.map((r) => r.id);
    const { data: schedules } = await supabase
      .from("schedules")
      .select("room_id, game_date")
      .in("room_id", roomIds);

    const scheduleMap = {};
    schedules?.forEach((s) => { scheduleMap[s.room_id] = s.game_date; });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = roomsData.filter((r) => {
      // 내가 나간 방 숨김 (left_teams 컬럼이 있을 때만 동작, 없으면 빈 배열로 간주)
      const left = Array.isArray(r.left_teams) ? r.left_teams : [];
      if (left.includes(teamName) || (emailPfx && left.includes(emailPfx))) return false;
      // 경기일 + 5일 지난 방 숨김
      const gameDate = scheduleMap[r.id];
      if (!gameDate) return true; // 일정 없으면 항상 표시
      const expiry = new Date(gameDate);
      expiry.setDate(expiry.getDate() + 5);
      return expiry >= today;
    });

    setRooms(filtered);

    // 방에 등장하는 팀들의 로고 일괄 조회
    if (filtered.length > 0) {
      const allNames = [...new Set(filtered.flatMap((r) => [r.team1_name, r.team2_name]))];
      const { data: teamData } = await supabase
        .from("teams")
        .select("team_name, logo_url")
        .in("team_name", allNames);
      const map = {};
      teamData?.forEach((t) => { if (t.logo_url) map[t.team_name] = t.logo_url; });
      setTeamLogoMap(map);
    }

    setLoading(false);
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      if (myTeam) await fetchRooms(myTeam, emailPrefix);
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY, myTeam, emailPrefix, fetchRooms]);

  useEffect(() => {
    if (myTeam) fetchRooms(myTeam, emailPrefix);
  }, [myTeam, emailPrefix, fetchRooms]);

  // 새 메시지 실시간 감지 → 목록 재정렬
  useEffect(() => {
    if (!myTeam) return;
    const channel = supabase
      .channel("chatlist-messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => {
        fetchRooms(myTeam, emailPrefix);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [myTeam, emailPrefix, fetchRooms]);

  return (
    <div ref={containerRef} style={{ ...styles.container, overflowY: "auto" }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#f7f8fa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...styles.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.logoRow}>
          <div style={{ width: 48 }} />
          <div style={styles.logoCenterWrap}>
            <span style={styles.logoText}>크루픽</span>
            <span style={styles.logoSub}>title</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <NotificationBell />
          </div>
        </div>
        <div style={styles.headerTitle}>채팅</div>
        <div style={styles.headerSub}>매칭이 성사된 팀과 대화하세요</div>
      </div>

      {!myTeam && (
        <div style={styles.emptyWrap}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>💬</div>
          <div style={{ fontSize: 16, color: "#6b7280", fontWeight: 600 }}>활성 팀을 설정해주세요</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 6, textAlign: "center", lineHeight: 1.7 }}>
            팀 관리 탭에서 팀을 선택하면<br />채팅방 목록이 표시돼요
          </div>
        </div>
      )}

      {myTeam && loading && (
        <div style={styles.emptyWrap}>
          <div style={styles.spinner} />
          <p style={{ color: "#9ca3af", fontSize: 14, marginTop: 16 }}>불러오는 중...</p>
        </div>
      )}

      {error && (
        <div style={styles.errorBox}>
          <span>⚠️</span>
          <span style={{ marginLeft: 8, fontSize: 13, color: "#dc2626" }}>{error}</span>
        </div>
      )}

      {myTeam && !loading && !error && (
        <div style={styles.listWrap}>
          <div style={{ background: "#fff8f8", borderLeft: "3px solid #E24B4A", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
            <span>💬</span> 매칭을 수락한 팀끼리만 채팅할 수 있어요
          </div>
          {rooms.length === 0 ? (
            <div style={styles.emptyWrap}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 15, color: "#6b7280", fontWeight: 600 }}>참여 중인 채팅방이 없어요</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6, textAlign: "center" }}>
                매칭이 수락되면 채팅방이 생성돼요
              </div>
            </div>
          ) : (
            rooms.map((room) => (
              <RoomCard
                key={room.id}
                room={room}
                myTeamName={myTeam}
                teamLogoMap={teamLogoMap}
                unreadCount={unreadByRoom[room.id] || 0}
                onClick={() => onRoomClick(room)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function RoomCard({ room, myTeamName, teamLogoMap = {}, unreadCount, onClick }) {
  const opponent = room.team1_name === myTeamName ? room.team2_name : room.team1_name;
  const sport = SPORTS[room.sports_type] || { icon: "🏅", label: room.sports_type || "-" };
  const lastAt = room.last_message_at
    ? new Date(room.last_message_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })
    : "";
  const hasUnread = unreadCount > 0;
  const opponentLogo = teamLogoMap[opponent];

  return (
    <div style={{
      ...styles.roomCard,
      borderColor: hasUnread ? "#fecaca" : "#e5e7eb",
      background: hasUnread ? "#fff8f8" : "#fff",
    }} onClick={onClick}>
      <div style={styles.roomIcon}>
        {opponentLogo
          ? <img src={opponentLogo} alt={opponent} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
          : <span style={{ fontSize: 20 }}>🏆</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...styles.roomName, color: hasUnread ? "#111827" : "#374151" }}>
          {opponent}
        </div>
        <div style={{
          ...styles.roomSub,
          color: hasUnread ? "#374151" : "#9ca3af",
          fontWeight: hasUnread ? 600 : 400,
        }}>
          {sport.label} · {room.last_message || "대화를 시작해보세요"}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        <div style={styles.roomTime}>{lastAt}</div>
        {hasUnread && (
          <span style={styles.unreadBadge}>
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f5f6fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header: { padding: "calc(env(safe-area-inset-top) + 16px) 20px 20px", borderBottom: "1px solid #e5e7eb", background: "#fff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  logoRow: { position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  homeBtn: { background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: "4px", lineHeight: 1 },
  logoCenterWrap: { position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "baseline", gap: 4 },
  logoText: { fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  logoSub:  { fontSize: 12, color: "#9ca3af", fontWeight: 400 },
  headerTitle: { fontSize: 22, fontWeight: 700, color: "#111827", letterSpacing: "-0.5px" },
  headerSub: { fontSize: 13, color: "#9ca3af", marginTop: 2 },
  listWrap: { flex: 1, padding: "12px 20px 32px", display: "flex", flexDirection: "column" },
  roomCard: { display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "#fff", borderRadius: 14, border: "1px solid #e5e7eb", cursor: "pointer", marginBottom: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  roomIcon: { width: 44, height: 44, background: "#f5f6fa", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, border: "1px solid #e5e7eb" },
  roomName: { fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 3 },
  roomSub: { fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  roomTime: { fontSize: 11, color: "#c4c9d4" },
  unreadBadge: {
    background: "#E24B4A", color: "#fff",
    fontSize: 11, fontWeight: 700, borderRadius: 999,
    minWidth: 20, height: 20, padding: "0 5px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  emptyWrap: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center" },
  errorBox: { display: "flex", alignItems: "center", margin: "12px 20px", padding: "12px 14px", background: "#fff5f5", borderRadius: 10, border: "1px solid #fecaca" },
  spinner: { width: 32, height: 32, border: "3px solid #e5e7eb", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};
